import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import ChatWidget from '../../home/components/ChatWidget';

interface Component {
  id: number;
  name: string;
  brand: string;
  category: 'cpu' | 'gpu' | 'memory' | 'storage' | 'motherboard' | 'cooling' | 'psu';
  categoryLabel: string;
  specs: {
    label: string;
    value: string;
  }[];
  price: number;
  originalPrice?: number;
  image: string;
  rating: number;
  stock: 'in-stock' | 'pre-order' | 'special-order';
  socket?: string;
  performance?: 'entry' | 'mainstream' | 'high-end' | 'enthusiast';
}

const ComponentsPage = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [components] = useState<Component[]>([
    {
      id: 1,
      name: 'Intel Core i9-13900K',
      brand: 'intel',
      category: 'cpu',
      categoryLabel: 'CPU',
      specs: [
        { label: isNL ? 'Kernen/Threads:' : 'Cores/Threads:', value: '24C/32T' },
        { label: isNL ? 'Basisklok:' : 'Base Clock:', value: '3.0 GHz' },
        { label: isNL ? 'Boostklok:' : 'Boost Clock:', value: '5.8 GHz' },
        { label: 'Socket:', value: 'LGA 1700' }
      ],
      price: 542.79,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/f4d1e17119e4bc68257b9d1121ecb677.png',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'lga1700',
      performance: 'enthusiast'
    },
    {
      id: 2,
      name: 'NVIDIA GeForce RTX 4080',
      brand: 'nvidia',
      category: 'gpu',
      categoryLabel: 'GPU',
      specs: [
        { label: isNL ? 'Geheugen:' : 'Memory:', value: '16GB GDDR6X' },
        { label: isNL ? 'Basisklok:' : 'Base Clock:', value: '2205 MHz' },
        { label: isNL ? 'Boostklok:' : 'Boost Clock:', value: '2505 MHz' },
        { label: isNL ? 'Aansluiting:' : 'Interface:', value: 'PCIe 4.0 x16' }
      ],
      price: 1103.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/dd3c099a5a3397f34735042b19fdd576.png',
      rating: 5.0,
      stock: 'in-stock',
      socket: 'pcie5',
      performance: 'high-end'
    },
    {
      id: 3,
      name: 'MSI GeForce RTX 4070 Ti GAMING X TRIO',
      brand: 'msi',
      category: 'gpu',
      categoryLabel: 'GPU',
      specs: [
        { label: isNL ? 'Geheugen:' : 'Memory:', value: '12GB GDDR6X' },
        { label: isNL ? 'Basisklok:' : 'Base Clock:', value: '2310 MHz' },
        { label: isNL ? 'Boostklok:' : 'Boost Clock:', value: '2610 MHz' },
        { label: isNL ? 'Aansluiting:' : 'Interface:', value: 'PCIe 4.0 x16' }
      ],
      price: 849.99,
      image: 'https://public.readdy.ai/ai/img_res/1361d674cf371d6bfd67bba823bf2f2c.jpg',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'pcie5',
      performance: 'high-end'
    },
    {
      id: 4,
      name: 'Corsair Vengeance DDR5-5600',
      brand: 'corsair',
      category: 'memory',
      categoryLabel: isNL ? 'Geheugen' : 'Memory',
      specs: [
        { label: isNL ? 'Capaciteit:' : 'Capacity:', value: '32GB (2x16GB)' },
        { label: isNL ? 'Snelheid:' : 'Speed:', value: 'DDR5-5600' },
        { label: isNL ? 'Timings:' : 'Timings:', value: 'CL36' },
        { label: 'RGB:', value: isNL ? 'Ja' : 'Yes' }
      ],
      price: 229.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/8114b1423c2fd59bb39b5256f46788c4.png',
      rating: 4.0,
      stock: 'in-stock',
      socket: 'ddr5',
      performance: 'high-end'
    },
    {
      id: 5,
      name: 'Samsung 980 PRO 2TB',
      brand: 'samsung',
      category: 'storage',
      categoryLabel: isNL ? 'Opslag' : 'Storage',
      specs: [
        { label: isNL ? 'Capaciteit:' : 'Capacity:', value: '2TB' },
        { label: isNL ? 'Aansluiting:' : 'Interface:', value: 'PCIe 4.0 x4' },
        { label: isNL ? 'Leessnelheid:' : 'Read Speed:', value: '7,000 MB/s' },
        { label: isNL ? 'Vormfactor:' : 'Form Factor:', value: 'M.2 2280' }
      ],
      price: 183.99,
      image: 'https://readdy.ai/api/search-image?query=Samsung%20980%20PRO%20NVMe%20M.2%20SSD%20solid%20state%20drive%20with%20black%20PCB%20circuit%20board%20showing%20gold%20connector%20pins%20and%20Samsung%20branding%20label%2C%20professional%20product%20photography%20on%20white%20background%20with%20detailed%20view%20of%20the%20compact%20M.2%202280%20form%20factor%20storage%20device&width=400&height=400&seq=samsung980pro2tb&orientation=squarish',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'pcie5',
      performance: 'high-end'
    },
    {
      id: 6,
      name: 'ASUS ROG Maximus Z790 Hero',
      brand: 'asus',
      category: 'motherboard',
      categoryLabel: isNL ? 'Moederbord' : 'Motherboard',
      specs: [
        { label: 'Socket:', value: 'LGA 1700' },
        { label: 'Chipset:', value: 'Intel Z790' },
        { label: isNL ? 'Geheugen:' : 'Memory:', value: 'DDR5-7200+' },
        { label: isNL ? 'Vormfactor:' : 'Form Factor:', value: 'ATX' }
      ],
      price: 579.59,
      image: 'https://readdy.ai/api/search-image?query=ASUS%20ROG%20Maximus%20Z790%20Hero%20premium%20gaming%20motherboard%20professional%20product%20photography%20showing%20ATX%20form%20factor%20with%20matte%20black%20PCB%20circuit%20board%20and%20brushed%20aluminum%20silver%20heatsinks%20covering%20VRM%20and%20chipset%20areas%2C%20visible%20LGA%201700%20CPU%20socket%20with%20protective%20cover%2C%20four%20DDR5%20DIMM%20memory%20slots%20with%20reinforced%20metal%20shielding%2C%20multiple%20PCIe%205.0%20expansion%20slots%20with%20steel%20reinforcement%2C%20integrated%20RGB%20lighting%20strips%20along%20edges%20and%20heatsinks%20displaying%20Republic%20of%20Gamers%20logo%20and%20branding%2C%20M.2%20SSD%20slots%20with%20dedicated%20heatsinks%2C%20premium%20capacitors%20and%20power%20delivery%20components%2C%20IO%20shield%20area%2C%20SATA%20ports%2C%20USB%20headers%2C%20clean%20white%20studio%20background%20with%20professional%20lighting%20highlighting%20the%20motherboard%20details%20and%20premium%20build%20quality&width=800&height=800&seq=asus-rog-z790-hero-motherboard-v4&orientation=squarish',
      rating: 4.0,
      stock: 'pre-order',
      socket: 'lga1700',
      performance: 'enthusiast'
    },
    {
      id: 7,
      name: 'AMD Ryzen 9 7950X',
      brand: 'amd',
      category: 'cpu',
      categoryLabel: 'CPU',
      specs: [
        { label: isNL ? 'Kernen/Threads:' : 'Cores/Threads:', value: '16C/32T' },
        { label: isNL ? 'Basisklok:' : 'Base Clock:', value: '4.5 GHz' },
        { label: isNL ? 'Boostklok:' : 'Boost Clock:', value: '5.7 GHz' },
        { label: 'Socket:', value: 'AM5' }
      ],
      price: 643.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/32bac446c1707222db0dfbc9d7d86a65.png',
      rating: 5.0,
      stock: 'in-stock',
      socket: 'am5',
      performance: 'enthusiast'
    },
    {
      id: 8,
      name: 'G.Skill Trident Z5 RGB DDR5-6000',
      brand: 'gskill',
      category: 'memory',
      categoryLabel: isNL ? 'Geheugen' : 'Memory',
      specs: [
        { label: isNL ? 'Capaciteit:' : 'Capacity:', value: '32GB (2x16GB)' },
        { label: isNL ? 'Snelheid:' : 'Speed:', value: 'DDR5-6000' },
        { label: isNL ? 'Timings:' : 'Timings:', value: 'CL30' },
        { label: 'RGB:', value: isNL ? 'Ja' : 'Yes' }
      ],
      price: 279.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/2e797c062755639dea797b4c207f1615.png',
      rating: 5.0,
      stock: 'in-stock',
      socket: 'ddr5',
      performance: 'enthusiast'
    },
    {
      id: 9,
      name: 'Corsair Vengeance RGB DDR5',
      brand: 'corsair',
      category: 'memory',
      categoryLabel: isNL ? 'Geheugen' : 'Memory',
      specs: [
        { label: isNL ? 'Capaciteit:' : 'Capacity:', value: '32GB (2x16GB)' },
        { label: isNL ? 'Snelheid:' : 'Speed:', value: 'DDR5-5600' },
        { label: isNL ? 'Timings:' : 'Timings:', value: 'CL36' },
        { label: 'RGB:', value: isNL ? 'Ja' : 'Yes' }
      ],
      price: 189.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/bbe950af4a7bc8313be0002356555a4e.png',
      rating: 4.0,
      stock: 'in-stock',
      socket: 'ddr5',
      performance: 'high-end'
    },
    {
      id: 10,
      name: 'WD Black SN850X 1TB',
      brand: 'western-digital',
      category: 'storage',
      categoryLabel: isNL ? 'Opslag' : 'Storage',
      specs: [
        { label: isNL ? 'Capaciteit:' : 'Capacity:', value: '1TB' },
        { label: isNL ? 'Aansluiting:' : 'Interface:', value: 'PCIe 4.0 x4' },
        { label: isNL ? 'Leessnelheid:' : 'Read Speed:', value: '7,300 MB/s' },
        { label: isNL ? 'Vormfactor:' : 'Form Factor:', value: 'M.2 2280' }
      ],
      price: 129.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/026e406a8b7c1fdb5374066cad11c127.png',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'pcie5',
      performance: 'high-end'
    },
    {
      id: 11,
      name: 'MSI MAG B650 TOMAHAWK',
      brand: 'msi',
      category: 'motherboard',
      categoryLabel: isNL ? 'Moederbord' : 'Motherboard',
      specs: [
        { label: 'Socket:', value: 'AM5' },
        { label: 'Chipset:', value: 'AMD B650' },
        { label: isNL ? 'Geheugen:' : 'Memory:', value: 'DDR5-6400+' },
        { label: isNL ? 'Vormfactor:' : 'Form Factor:', value: 'ATX' }
      ],
      price: 249.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/6e176556b9cfd2dc2619bfcdcf0f60d9.png',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'am5',
      performance: 'mainstream'
    },
    {
      id: 12,
      name: 'Intel Core i7-13700K',
      brand: 'intel',
      category: 'cpu',
      categoryLabel: 'CPU',
      specs: [
        { label: isNL ? 'Kernen/Threads:' : 'Cores/Threads:', value: '16C/24T' },
        { label: isNL ? 'Basisklok:' : 'Base Clock:', value: '3.4 GHz' },
        { label: isNL ? 'Boostklok:' : 'Boost Clock:', value: '5.4 GHz' },
        { label: 'Socket:', value: 'LGA 1700' }
      ],
      price: 419.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/4cfbb70316673d405b16fadddf68df59.png',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'lga1700',
      performance: 'high-end'
    },
    {
      id: 13,
      name: 'AMD Radeon RX 7900 XTX',
      brand: 'amd',
      category: 'gpu',
      categoryLabel: 'GPU',
      specs: [
        { label: isNL ? 'Geheugen:' : 'Memory:', value: '24GB GDDR6' },
        { label: isNL ? 'Basisklok:' : 'Base Clock:', value: '2300 MHz' },
        { label: isNL ? 'Boostklok:' : 'Boost Clock:', value: '2500 MHz' },
        { label: isNL ? 'Aansluiting:' : 'Interface:', value: 'PCIe 4.0 x16' }
      ],
      price: 999.99,
      image: 'https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/99bff8ccc335c88229e0c0af076c2ab8.png',
      rating: 4.5,
      stock: 'in-stock',
      socket: 'pcie5',
      performance: 'enthusiast'
    }
  ]);

  const [filteredComponents, setFilteredComponents] = useState<Component[]>(components);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedSockets, setSelectedSockets] = useState<string[]>([]);
  const [selectedPerformance, setSelectedPerformance] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(4600);
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [compareList, setCompareList] = useState<number[]>([]);

  useEffect(() => {
    let filtered = components.filter(component => {
      const matchesCategory = selectedCategory === 'all' || component.category === selectedCategory;
      const matchesBrand = selectedBrands.length === 0 || selectedBrands.includes(component.brand);
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(component.category);
      const matchesSocket = selectedSockets.length === 0 || (component.socket && selectedSockets.includes(component.socket));
      const matchesPerformance = selectedPerformance.length === 0 || (component.performance && selectedPerformance.includes(component.performance));
      const matchesAvailability = selectedAvailability.length === 0 || selectedAvailability.includes(component.stock);
      const matchesPrice = component.price >= priceMin && component.price <= priceMax;

      return matchesCategory && matchesBrand && matchesType && matchesSocket && 
             matchesPerformance && matchesAvailability && matchesPrice;
    });

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'popularity':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
    }

    setFilteredComponents(filtered);
  }, [components, selectedCategory, selectedBrands, selectedTypes, selectedSockets, 
      selectedPerformance, selectedAvailability, priceMin, priceMax, sortBy]);

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedBrands([]);
    setSelectedTypes([]);
    setSelectedSockets([]);
    setSelectedPerformance([]);
    setSelectedAvailability([]);
    setPriceMin(0);
    setPriceMax(4600);
  };

  const handleFilterChange = (filterType: string, value: string, checked: boolean) => {
    switch (filterType) {
      case 'brand':
        setSelectedBrands(prev => checked ? [...prev, value] : prev.filter(item => item !== value));
        break;
      case 'type':
        setSelectedTypes(prev => checked ? [...prev, value] : prev.filter(item => item !== value));
        break;
      case 'socket':
        setSelectedSockets(prev => checked ? [...prev, value] : prev.filter(item => item !== value));
        break;
      case 'performance':
        setSelectedPerformance(prev => checked ? [...prev, value] : prev.filter(item => item !== value));
        break;
      case 'availability':
        setSelectedAvailability(prev => checked ? [...prev, value] : prev.filter(item => item !== value));
        break;
    }
  };

  const toggleCompare = (id: number) => {
    setCompareList(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else if (prev.length < 4) {
        return [...prev, id];
      }
      return prev;
    });
  };

  const getStockBadge = (stock: string) => {
    switch (stock) {
      case 'in-stock':
        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Op Voorraad' : 'In Stock'}</span>;
      case 'pre-order':
        return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Voorbestelling' : 'Pre-Order'}</span>;
      case 'special-order':
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Speciale Bestelling' : 'Special Order'}</span>;
      default:
        return null;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      cpu: 'text-blue-600 bg-blue-50',
      gpu: 'text-green-600 bg-green-50',
      memory: 'text-purple-600 bg-purple-50',
      storage: 'text-orange-600 bg-orange-50',
      motherboard: 'text-red-600 bg-red-50',
      cooling: 'text-cyan-600 bg-cyan-50',
      psu: 'text-yellow-600 bg-yellow-50'
    };
    return colors[category] || 'text-gray-600 bg-gray-50';
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <div key={i} className="w-4 h-4 flex items-center justify-center">
        <i className={`${i < Math.floor(rating) ? 'ri-star-fill' : i < rating ? 'ri-star-half-fill' : 'ri-star-line'} text-yellow-400`}></i>
      </div>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-teal-600 flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-teal-600">Hardware</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">{isNL ? 'Componenten' : 'Components'}</span>
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start">
            <div className="max-w-4xl">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{isNL ? 'Computercomponenten' : 'Computer Components'}</h1>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Professionele computercomponenten voor op maat gemaakte systemen, upgrades en systeemoptimalisatie. Vind processoren, grafische kaarten, geheugen, opslag en meer.'
                  : 'Professional-grade computer components for custom builds, upgrades, and system optimization. Find CPUs, GPUs, memory, storage, and more.'}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{isNL ? 'Componentcategorieën' : 'Component Categories'}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-line text-teal-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Processoren' : 'Processors'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-fill text-green-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Grafische Kaarten' : 'Graphics Cards'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-database-2-line text-teal-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Geheugen & Opslag' : 'Memory & Storage'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-computer-line text-purple-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Moederborden' : 'Motherboards'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{isNL ? 'Toonaangevende Merken' : 'Leading Brands'}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-line text-[#0071C5] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Intel</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-fill text-[#ED1C24] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">AMD</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-line text-[#76B900] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">NVIDIA</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-computer-line text-[#1BA0D7] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">ASUS</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                <button 
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'all' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory('all')}
                >
                  {isNL ? 'Alle Componenten' : 'All Components'}
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'cpu' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory('cpu')}
                >
                  {isNL ? 'Processoren' : 'CPUs'}
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'gpu' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory('gpu')}
                >
                  {isNL ? 'Grafische Kaarten' : 'Graphics Cards'}
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'memory' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory('memory')}
                >
                  {isNL ? 'Geheugen' : 'Memory'}
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'storage' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory('storage')}
                >
                  {isNL ? 'Opslag' : 'Storage'}
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'motherboard' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory('motherboard')}
                >
                  {isNL ? 'Moederborden' : 'Motherboards'}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                className="flex items-center text-gray-600 hover:text-teal-600"
                onClick={() => {
                  const bar = document.getElementById('comparison-bar');
                  if (bar) {
                    bar.classList.toggle('translate-y-full');
                  }
                }}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-scales-3-line"></i>
                </div>
                {isNL ? 'Vergelijken' : 'Compare'} ({compareList.length})
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                <button 
                  className={`p-2 rounded ${viewMode === 'grid' ? 'text-teal-600 bg-teal-50' : 'text-gray-400 hover:text-gray-600'}`}
                  onClick={() => setViewMode('grid')}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </div>
                </button>
                <button 
                  className={`p-2 rounded ${viewMode === 'list' ? 'text-teal-600 bg-teal-50' : 'text-gray-400 hover:text-gray-600'}`}
                  onClick={() => setViewMode('list')}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-list-check"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-40">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                <button 
                  className="text-teal-600 text-sm hover:underline"
                  onClick={clearAllFilters}
                >
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
              </div>

              {/* Component Type */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Componenttype' : 'Component Type'}</h4>
                <div className="space-y-2">
                  {[
                    { value: 'cpu', label: isNL ? 'Processoren (CPU\'s)' : 'Processors (CPUs)' },
                    { value: 'gpu', label: isNL ? 'Grafische Kaarten' : 'Graphics Cards' },
                    { value: 'memory', label: isNL ? 'Geheugen (RAM)' : 'Memory (RAM)' },
                    { value: 'storage', label: isNL ? 'Opslagapparaten' : 'Storage Devices' },
                    { value: 'motherboard', label: isNL ? 'Moederborden' : 'Motherboards' },
                    { value: 'cooling', label: isNL ? 'Koeloplossingen' : 'Cooling Solutions' },
                    { value: 'psu', label: isNL ? 'Voedingen' : 'Power Supplies' }
                  ].map(type => (
                    <label key={type.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        checked={selectedTypes.includes(type.value)}
                        onChange={(e) => handleFilterChange('type', type.value, e.target.checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brand */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Merk' : 'Brand'}</h4>
                <div className="space-y-2">
                  {['intel', 'amd', 'nvidia', 'asus', 'msi', 'corsair', 'samsung'].map(brand => (
                    <label key={brand} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        checked={selectedBrands.includes(brand)}
                        onChange={(e) => handleFilterChange('brand', brand, e.target.checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700 capitalize">{brand === 'gskill' ? 'G.Skill' : brand === 'western-digital' ? 'Western Digital' : brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Socket/Compatibility */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Socket/Compatibiliteit' : 'Socket/Compatibility'}</h4>
                <div className="space-y-2">
                  {[
                    { value: 'lga1700', label: 'LGA 1700 (Intel)' },
                    { value: 'am5', label: 'AM5 (AMD)' },
                    { value: 'am4', label: 'AM4 (AMD)' },
                    { value: 'pcie5', label: 'PCIe 5.0' },
                    { value: 'ddr5', label: isNL ? 'DDR5 Compatibel' : 'DDR5 Compatible' }
                  ].map(socket => (
                    <label key={socket.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        checked={selectedSockets.includes(socket.value)}
                        onChange={(e) => handleFilterChange('socket', socket.value, e.target.checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700">{socket.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Performance Level */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Prestatieniveau' : 'Performance Level'}</h4>
                <div className="space-y-2">
                  {[
                    { value: 'entry', label: isNL ? 'Instapniveau' : 'Entry Level' },
                    { value: 'mainstream', label: 'Mainstream' },
                    { value: 'high-end', label: 'High-End' },
                    { value: 'enthusiast', label: 'Enthusiast' }
                  ].map(perf => (
                    <label key={perf.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        checked={selectedPerformance.includes(perf.value)}
                        onChange={(e) => handleFilterChange('performance', perf.value, e.target.checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700">{perf.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Beschikbaarheid' : 'Availability'}</h4>
                <div className="space-y-2">
                  {[
                    { value: 'in-stock', label: isNL ? 'Op Voorraad' : 'In Stock' },
                    { value: 'pre-order', label: isNL ? 'Voorbestelling' : 'Pre-Order' },
                    { value: 'special-order', label: isNL ? 'Speciale Bestelling' : 'Special Order' }
                  ].map(avail => (
                    <label key={avail.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        checked={selectedAvailability.includes(avail.value)}
                        onChange={(e) => handleFilterChange('availability', avail.value, e.target.checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700">{avail.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {isNL ? (
                    <><span className="font-medium">{filteredComponents.length}</span> van {components.length} componenten weergegeven</>
                  ) : (
                    <>Showing <span className="font-medium">{filteredComponents.length}</span> of {components.length} components</>
                  )}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select
                    className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-8"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                    <option value="price-low">{isNL ? 'Prijs: Laag naar Hoog' : 'Price: Low to High'}</option>
                    <option value="price-high">{isNL ? 'Prijs: Hoog naar Laag' : 'Price: High to Low'}</option>
                    <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Toon:' : 'Show:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-8">
                    <option value="18">18</option>
                    <option value="36" selected>36</option>
                    <option value="72">72</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {filteredComponents.map((component) => (
                <div
                  key={component.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <div className="h-48 overflow-hidden bg-white flex items-center justify-center p-4">
                      <img
                        src={component.image}
                        alt={component.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColor(component.category)}`}>
                        {component.categoryLabel}
                      </span>
                      <div className="text-yellow-400 flex">
                        {renderStars(component.rating)}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{component.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{component.brand.charAt(0).toUpperCase() + component.brand.slice(1)}</p>
                    <div className="space-y-2 mb-4">
                      {component.specs.map((spec, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{spec.label}</span>
                          <span className="font-medium">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs text-gray-500">{isNL ? 'Neem contact op voor prijzen' : 'Contact us for pricing'}</div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="bg-gray-100 text-gray-600 px-3 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-gray-200 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <a href={`${prefix}/contact`} className="bg-teal-600 text-white px-3 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-teal-700 cursor-pointer">
                          {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredComponents.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line ri-2x"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{isNL ? 'Geen componenten gevonden' : 'No components found'}</h3>
                <p className="text-gray-600 mb-4">{isNL ? 'Probeer uw filters aan te passen' : 'Try adjusting your filters'}</p>
                <button 
                  onClick={clearAllFilters}
                  className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700"
                >
                  {isNL ? 'Alle Filters Wissen' : 'Clear All Filters'}
                </button>
              </div>
            )}

            {/* Pagination */}
            {filteredComponents.length > 0 && (
              <div className="flex justify-between items-center mt-12">
                <div className="text-gray-600">
                  {isNL
                    ? `1-${filteredComponents.length} van ${components.length} resultaten weergegeven`
                    : `Showing 1-${filteredComponents.length} of ${components.length} results`}
                </div>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50" disabled>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </div>
                  </button>
                  <button className="px-3 py-2 bg-teal-600 text-white rounded">1</button>
                  <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">2</button>
                  <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">3</button>
                  <span className="px-2 text-gray-500">...</span>
                  <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">7</button>
                  <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comparison Bar */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform translate-y-full transition-transform duration-300 z-40"
        id="comparison-bar"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="font-medium">{isNL ? 'Componenten Vergelijken' : 'Compare Components'} ({compareList.length}/4)</span>
              <div className="flex space-x-2">
                {compareList.map(id => {
                  const comp = components.find(c => c.id === id);
                  return comp ? (
                    <div key={id} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center space-x-2">
                      <span>{comp.name}</span>
                      <button onClick={() => toggleCompare(id)} className="text-gray-500 hover:text-gray-700">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-close-line"></i>
                        </div>
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                className="text-gray-600 hover:text-gray-800"
                onClick={() => setCompareList([])}
              >
                {isNL ? 'Alles Wissen' : 'Clear All'}
              </button>
              <button 
                className="bg-teal-600 text-white px-6 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-teal-700"
                disabled={compareList.length < 2}
              >
                {isNL ? 'Selectie Vergelijken' : 'Compare Selected'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ComponentsPage;
